/**
 * Страница «Партнёры» (v0.9.63 #9.66).
 * Группировка организаций по partner_inn → таблица.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Handshake, Loader2, Users } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../lib/auth';

interface Client { id: string; name: string; inn: string; project_status: string | null;
                   contract_active: boolean; industry: string | null }
interface Partner {
  partner_inn: string; partner_org: string; partner_contact_name: string;
  partner_contact_email: string; partner_contact_phone: string;
  clients: Client[]; total_clients: number;
  pilots: number; support: number; no_support: number;
}

export default function PartnersPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ partners: Partner[]; total: number }>('/api/v1/partners')
      .then(r => setPartners(r.data.partners))
      .catch(e => setErr(e?.response?.data?.detail || e?.message || 'Ошибка'))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;
  if (user.type !== 'staff') return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Handshake className="text-amber-400" size={20} />
        <h1 className="text-2xl font-bold text-white">Партнёры</h1>
        <span className="text-xs text-slate-500">источники лидов и исполнители</span>
      </div>
      <div className="text-xs text-slate-500">
        Группируются по ИНН партнёра (заполняется автоматически при миграции
        из BC.Pilot или вручную в карточке организации, секция «Партнёр»).
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : err ? (
        <div className="bg-rose-900/30 border border-rose-800 text-rose-200 px-3 py-2 rounded">{err}</div>
      ) : partners.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center text-slate-500 text-sm">
          Партнёров пока нет. Поля «Партнёр» в карточке организации заполнятся
          автоматически при миграции из BC.Pilot, если в воронке указан
          partnerPresent=true.
        </div>
      ) : (
        <div className="space-y-3">
          {partners.map(p => (
            <div key={p.partner_inn} className="bg-slate-900 border border-slate-800 rounded-xl">
              <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-white font-bold">{p.partner_org || `ИНН ${p.partner_inn}`}</div>
                  <div className="text-xs text-slate-500 font-mono">ИНН {p.partner_inn}</div>
                  {p.partner_contact_name && (
                    <div className="text-xs text-slate-400 mt-0.5">
                      {p.partner_contact_name}
                      {p.partner_contact_email && (
                        <a href={`mailto:${p.partner_contact_email}`} className="ml-2 text-blue-300 hover:underline">
                          {p.partner_contact_email}
                        </a>
                      )}
                      {p.partner_contact_phone && (
                        <a href={`tel:${p.partner_contact_phone}`} className="ml-2 text-slate-300 hover:text-blue-300">
                          {p.partner_contact_phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 text-xs">
                  <Stat label="клиентов" value={p.total_clients} color="text-slate-200" icon={Users} />
                  <Stat label="пилотов" value={p.pilots} color="text-sky-300" />
                  <Stat label="на поддержке" value={p.support} color="text-emerald-300" />
                  <Stat label="без поддержки" value={p.no_support} color="text-rose-300" />
                </div>
              </header>
              <div className="p-3">
                <ul className="space-y-1">
                  {p.clients.map(c => (
                    <li key={c.id} className="flex items-center justify-between gap-2 text-sm py-1 px-2 rounded hover:bg-slate-800/40">
                      <Link to={`/organizations/${c.id}`} className="text-blue-300 hover:underline flex-1 truncate">
                        {c.name}
                      </Link>
                      <span className="text-[10px] text-slate-500 font-mono">{c.inn}</span>
                      {c.industry && <span className="text-[10px] text-slate-500">{c.industry}</span>}
                      {c.project_status && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          c.project_status === 'pilot' ? 'bg-sky-900/50 text-sky-300' :
                          c.project_status === 'support' ? 'bg-emerald-900/50 text-emerald-300' :
                          'bg-slate-700 text-slate-400'
                        }`}>
                          {c.project_status === 'pilot' ? 'пилот' :
                           c.project_status === 'support' ? 'поддержка' : 'без поддержки'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, icon: Icon }: { label: string; value: number; color: string; icon?: any }) {
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 bg-slate-800 rounded ${color}`}>
      {Icon && <Icon size={11} />}
      <span className="font-bold">{value}</span>
      <span className="opacity-70 text-[10px]">{label}</span>
    </div>
  );
}
